import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Calendar, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useUser } from '@/providers/UserProvider';
import TiktokIcon from '@/components/ui/TiktokIcon';
import { Instagram } from 'lucide-react';

const CreateFlowModal = ({ open, onOpenChange, onCreateFlow }) => {
  const [name, setName] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [scheduling, setScheduling] = useState('manual');
  const { user } = useUser();

  const platforms = [
    { id: 'tiktok', name: 'TikTok', icon: <TiktokIcon className="w-4 h-4 mr-2" /> },
    { id: 'instagram', name: 'Instagram', icon: <Instagram className="w-4 h-4 mr-2" /> }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    onCreateFlow({
      name,
      platforms: selectedPlatforms,
      scheduling,
      type: 'cross-post',
      videos: [],
      created_at: new Date().toISOString()
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] bg-gray-900 border-gray-800 text-gray-100">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">Create Cross-Post</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6 py-4">
          {/* Name Input */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-200">
              Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700 rounded-full text-gray-100 
                        focus:ring-2 focus:ring-purple-500/30 focus:border-purple-400
                        transition-all duration-200"
              placeholder="Enter name..."
              required
            />
          </div>

          {/* Platform Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-200">
              Target Platforms
            </label>
            <div className="space-y-2">
              {platforms.map((platform) => (
                <label
                  key={platform.id}
                  className={cn(
                    "flex items-center p-3 rounded-full border cursor-pointer transition-all duration-200",
                    selectedPlatforms.includes(platform.id)
                      ? "bg-purple-500/20 border-purple-500/50 text-white"
                      : "bg-gray-800/50 border-gray-700 text-gray-300 hover:bg-gray-800 hover:border-gray-600"
                  )}
                >
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-purple-500 rounded-full border-gray-700 bg-gray-800 
                              focus:ring-purple-500 focus:ring-offset-gray-900"
                    checked={selectedPlatforms.includes(platform.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedPlatforms([...selectedPlatforms, platform.id]);
                      } else {
                        setSelectedPlatforms(selectedPlatforms.filter(id => id !== platform.id));
                      }
                    }}
                  />
                  <div className="ml-3 flex items-center">
                    {platform.icon}
                    <span>{platform.name}</span>
                  </div>
                </label>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Select the platforms where you want to cross-post your videos
            </p>
          </div>

          {/* Scheduling Options */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-200">
              Scheduling
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                className={cn(
                  "p-3 rounded-full border text-sm font-medium transition-all duration-200",
                  scheduling === 'manual' 
                    ? "bg-purple-500 border-purple-400 text-white"
                    : "bg-gray-800/50 border-gray-700 text-gray-300 hover:bg-gray-800 hover:border-gray-600"
                )}
                onClick={() => setScheduling('manual')}
              >
                <Calendar className="w-4 h-4 mx-auto mb-1" />
                Manual
              </button>
              <button
                type="button"
                className={cn(
                  "p-3 rounded-full border text-sm font-medium transition-all duration-200",
                  scheduling === 'automated' 
                    ? "bg-purple-500 border-purple-400 text-white"
                    : "bg-gray-800/50 border-gray-700 text-gray-300 hover:bg-gray-800 hover:border-gray-600"
                )}
                onClick={() => setScheduling('automated')}
              >
                <Zap className="w-4 h-4 mx-auto mb-1" />
                Automated
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full bg-purple-500 hover:bg-purple-600 text-white rounded-full h-10"
            disabled={!name || selectedPlatforms.length === 0}
          >
            Create Cross-Post
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateFlowModal; 